import { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';
import useMobile from '../../hooks/useMobile';

const C = {
  blue: '#6699ff',
  blueDark: '#4d7ae6',
  navy: '#0b1120',
  navyMid: '#162040',
  cream: '#f5f0e8',
  creamLight: '#faf7f2',
  creamWarm: '#ede5d8',
  textDark: '#0d1520',
  textBody: '#3a4860',
  textMuted: '#536179',
  white: '#ffffff',
};

export default function PublicationsPage() {
  const [publications, setPublications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [activeYear, setActiveYear] = useState('all');
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewTitle, setPreviewTitle] = useState('');
  const isMobile = useMobile();

  useEffect(() => {
    fetchPublications();
  }, []);

  const fetchPublications = async () => {
    const { data } = await supabase
      .from('publications')
      .select('*')
      .eq('status', 'published')
      .order('year', { ascending: false });
    if (data) setPublications(data);
    setLoading(false);
  };

  const categories = ['all', ...new Set(publications.map(p => p.category).filter(Boolean))];
  const years = ['all', ...new Set(publications.map(p => p.year).filter(Boolean))].sort((a, b) => {
    if (a === 'all') return -1;
    if (b === 'all') return 1;
    return b - a;
  });

  const filtered = publications.filter(p => {
    if (activeCategory !== 'all' && p.category !== activeCategory) return false;
    if (activeYear !== 'all' && p.year !== parseInt(activeYear)) return false;
    if (search) {
      const s = search.toLowerCase();
      const matchTitle = p.title.toLowerCase().includes(s);
      const matchDesc = (p.description || '').toLowerCase().includes(s);
      const matchTags = (p.tags || []).some(t => t.toLowerCase().includes(s));
      if (!matchTitle && !matchDesc && !matchTags) return false;
    }
    return true;
  });

  const getPublicUrl = (filePath) => {
    const { data } = supabase.storage.from('publications').getPublicUrl(filePath);
    return data.publicUrl;
  };

  const openPreview = (pub) => {
    setPreviewUrl(getPublicUrl(pub.file_path));
    setPreviewTitle(pub.title);
  };

  return (
    <div style={{ minHeight: '100vh', background: C.creamLight, fontFamily: "'Manrope', sans-serif" }}>
      {/* Nav */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        padding: isMobile ? '0 16px' : '0 60px', height: isMobile ? 56 : 72,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: 'rgba(245,240,232,0.95)', backdropFilter: 'blur(24px)',
        borderBottom: `1px solid ${C.creamWarm}`,
      }}>
        <a href="/" style={{ textDecoration: 'none' }}>
          <span style={{ fontSize: isMobile ? 11 : 12, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase', color: C.navy }}>
            SPC <span style={{ color: C.blue }}>Publications</span>
          </span>
        </a>
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 16 : 32 }}>
          <a href="/" style={{ fontSize: isMobile ? 11 : 12, fontWeight: 500, letterSpacing: 1.5, textTransform: 'uppercase', color: C.textBody, textDecoration: 'none' }}>Home</a>
          {!isMobile && <a href="/articles" style={{ fontSize: 12, fontWeight: 500, letterSpacing: 1.5, textTransform: 'uppercase', color: C.textBody, textDecoration: 'none' }}>Articles</a>}
          <a href="/publications" style={{ fontSize: isMobile ? 11 : 12, fontWeight: 500, letterSpacing: 1.5, textTransform: 'uppercase', color: C.blue, textDecoration: 'none' }}>Publications</a>
        </div>
      </nav>

      {/* Hero */}
      <div style={{ background: C.navy, padding: isMobile ? '100px 24px 48px' : '140px 80px 80px', position: 'relative', overflow: 'hidden' }}>
        <div style={{
          position: 'absolute', width: 600, height: 600, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(102,153,255,0.06) 0%, transparent 60%)',
          top: -200, right: -100,
        }} />
        <div style={{ maxWidth: 1200, margin: '0 auto', position: 'relative', zIndex: 2 }}>
          <div style={{
            fontSize: isMobile ? 9 : 10, fontWeight: 700, letterSpacing: isMobile ? 3 : 4, textTransform: 'uppercase',
            color: C.blue, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12,
            animation: 'fadeInLeft 0.8s ease 0.1s both',
          }}>
            <span style={{ height: 1, background: C.blue, display: 'inline-block', width: 32 }} />
            Technical Library
          </div>
          <h1 style={{
            fontFamily: "'Cormorant Garamond', serif", fontSize: isMobile ? 36 : 56, fontWeight: 400,
            color: C.cream, lineHeight: 1.1, marginBottom: 16,
            animation: 'fadeInLeft 0.9s ease 0.3s both',
          }}>
            Publications & <em style={{ fontStyle: 'italic', color: C.creamWarm }}>Resources</em>
          </h1>
          <p style={{
            fontSize: isMobile ? 14 : 16, lineHeight: 1.7, color: 'rgba(245,240,232,0.5)', maxWidth: 540,
            animation: 'fadeInUp 0.8s ease 0.6s both',
          }}>
            Access our library of technical papers, case studies, industry reports, and training materials from over 30 years of iron & steel consulting experience.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: isMobile ? '24px 16px 0' : '32px 80px 0' }}>
        {/* Search */}
        <div style={{ marginBottom: 20 }}>
          <label htmlFor="search-publications" className="sr-only">Search publications</label>
          <input
            id="search-publications"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search publications..."
            style={{
              width: '100%', maxWidth: isMobile ? '100%' : 400, padding: '12px 16px',
              border: `1px solid ${C.creamWarm}`, background: C.white,
              fontFamily: "'Manrope', sans-serif", fontSize: 16,
              color: C.textDark, outline: 'none',
            }}
            onFocus={e => { e.target.style.borderColor = C.blue; }}
            onBlur={e => { e.target.style.borderColor = C.creamWarm; }}
          />
        </div>

        {/* Category + Year filters */}
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 16 : 24, flexWrap: 'wrap', marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: C.textMuted, marginBottom: 8 }}>Category</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  style={{
                    padding: isMobile ? '6px 12px' : '6px 16px',
                    border: `1px solid ${activeCategory === cat ? C.blue : C.creamWarm}`,
                    background: activeCategory === cat ? 'rgba(102,153,255,0.08)' : C.white,
                    color: activeCategory === cat ? C.blue : C.textBody,
                    fontFamily: "'Manrope', sans-serif", fontSize: isMobile ? 10 : 11, fontWeight: 600,
                    letterSpacing: 1, textTransform: 'uppercase', cursor: 'pointer',
                  }}
                >
                  {cat === 'all' ? 'All' : cat}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: C.textMuted, marginBottom: 8 }}>Year</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {years.map(y => (
                <button
                  key={y}
                  onClick={() => setActiveYear(y.toString())}
                  style={{
                    padding: isMobile ? '6px 12px' : '6px 16px',
                    border: `1px solid ${activeYear === y.toString() ? C.blue : C.creamWarm}`,
                    background: activeYear === y.toString() ? 'rgba(102,153,255,0.08)' : C.white,
                    color: activeYear === y.toString() ? C.blue : C.textBody,
                    fontFamily: "'Manrope', sans-serif", fontSize: isMobile ? 10 : 11, fontWeight: 600,
                    letterSpacing: 1, cursor: 'pointer',
                  }}
                >
                  {y === 'all' ? 'All Years' : y}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Results count */}
        <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${C.creamWarm}`, fontSize: 12, color: C.textMuted }}>
          {filtered.length} publication{filtered.length !== 1 ? 's' : ''} found
          {(activeCategory !== 'all' || activeYear !== 'all' || search) && (
            <button
              onClick={() => { setActiveCategory('all'); setActiveYear('all'); setSearch(''); }}
              style={{
                marginLeft: 12, background: 'none', border: 'none',
                color: C.blue, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                fontFamily: "'Manrope', sans-serif",
              }}
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Publications Grid */}
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: isMobile ? '24px 16px 60px' : '32px 80px 100px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 80, color: C.textMuted }}>Loading publications...</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 80 }}>
            <div style={{ fontSize: 18, color: C.textMuted, marginBottom: 8 }}>
              {publications.length === 0 ? 'No publications yet' : 'No publications match your filters'}
            </div>
            <p style={{ fontSize: 14, color: C.textMuted }}>
              {publications.length === 0 ? 'Check back soon for technical papers and resources.' : 'Try adjusting your search or filters.'}
            </p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: isMobile ? 16 : 24 }}>
            {filtered.map((pub, index) => (
              <PublicationCard key={pub.id} pub={pub} index={index} onPreview={() => openPreview(pub)} getUrl={getPublicUrl} />
            ))}
          </div>
        )}
      </div>

      {/* PDF Preview Modal */}
      {previewUrl && (
        <div
          onClick={() => { setPreviewUrl(null); setPreviewTitle(''); }}
          style={{
            position: 'fixed', inset: 0, zIndex: 200,
            background: 'rgba(11,17,32,0.85)', backdropFilter: 'blur(8px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: isMobile ? 12 : 40,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: '100%', maxWidth: 900, height: isMobile ? '95vh' : '85vh',
              background: C.white, display: 'flex', flexDirection: 'column',
              boxShadow: '0 32px 80px rgba(0,0,0,0.4)',
            }}
          >
            <div style={{
              padding: isMobile ? '12px 16px' : '16px 24px', borderBottom: `1px solid ${C.creamWarm}`,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              flexWrap: isMobile ? 'wrap' : 'nowrap', gap: 8,
            }}>
              <span style={{ fontSize: isMobile ? 12 : 14, fontWeight: 600, color: C.textDark }}>{previewTitle}</span>
              <div style={{ display: 'flex', gap: 8 }}>
                <a href={previewUrl} download target="_blank" rel="noreferrer" style={{
                  padding: isMobile ? '6px 14px' : '8px 20px', background: C.blue, color: C.navy, textDecoration: 'none',
                  fontFamily: "'Manrope', sans-serif", fontSize: 11, fontWeight: 700,
                  letterSpacing: 1.5, textTransform: 'uppercase',
                }}>
                  Download
                </a>
                <button
                  onClick={() => { setPreviewUrl(null); setPreviewTitle(''); }}
                  style={{
                    padding: isMobile ? '6px 12px' : '8px 16px', background: 'none', border: `1px solid ${C.creamWarm}`,
                    color: C.textBody, fontFamily: "'Manrope', sans-serif", fontSize: 14,
                    cursor: 'pointer',
                  }}
                >
                  ✕
                </button>
              </div>
            </div>
            <iframe
              src={previewUrl}
              title={previewTitle}
              style={{ flex: 1, border: 'none', width: '100%' }}
            />
          </div>
        </div>
      )}

      {/* Footer */}
      <div style={{
        background: C.navy, padding: isMobile ? '32px 16px' : '40px 80px',
        display: 'flex', flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between', alignItems: isMobile ? 'center' : 'center',
        gap: isMobile ? 8 : 0,
      }}>
        <p style={{ fontSize: 12, color: 'rgba(245,240,232,0.25)' }}>© 2026 Steel Process Consultancy Inc.</p>
        <a href="/" style={{ fontSize: 12, color: 'rgba(245,240,232,0.4)', textDecoration: 'none' }}>Back to main site</a>
      </div>
    </div>
  );
}

function PublicationCard({ pub, index, onPreview, getUrl }) {
  const [hovered, setHovered] = useState(false);

  const formatSize = (bytes) => {
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: C.white, border: `1px solid ${C.creamWarm}`,
        overflow: 'hidden', transition: 'all 0.4s ease',
        transform: hovered ? 'translateY(-4px)' : 'translateY(0)',
        boxShadow: hovered ? '0 16px 48px rgba(10,20,40,0.08)' : 'none',
        animation: `fadeInUp 0.6s ease ${0.2 + (index * 0.1)}s both`,
      }}
    >
      {pub.cover_image ? (
        <div style={{ width: '100%', height: 180, overflow: 'hidden' }}>
          <img src={pub.cover_image} alt={pub.title} style={{
            width: '100%', height: '100%', objectFit: 'cover',
            transition: 'transform 0.4s ease',
            transform: hovered ? 'scale(1.05)' : 'scale(1)',
          }} />
        </div>
      ) : (
        <div style={{
          width: '100%', height: 180, background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 8,
        }}>
          <span style={{ fontSize: 32, color: 'rgba(102,153,255,0.15)' }}>PDF</span>
          <span style={{ fontSize: 11, color: 'rgba(102,153,255,0.3)', letterSpacing: 2, textTransform: 'uppercase' }}>{pub.year}</span>
        </div>
      )}

      <div style={{ padding: '20px 24px 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <span style={{
            padding: '2px 8px', background: 'rgba(102,153,255,0.08)',
            fontSize: 10, fontWeight: 700, color: C.blue, letterSpacing: 1, textTransform: 'uppercase',
          }}>
            PDF
          </span>
          {pub.category && (
            <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', color: C.textMuted }}>
              {pub.category}
            </span>
          )}
        </div>

        <h3 style={{
          fontFamily: "'Cormorant Garamond', serif", fontSize: 20, fontWeight: 600,
          color: hovered ? C.blue : C.textDark, lineHeight: 1.3, marginBottom: 8,
          transition: 'color 0.3s ease',
        }}>
          {pub.title}
        </h3>

        {pub.description && (
          <p style={{ fontSize: 13, lineHeight: 1.6, color: C.textBody, marginBottom: 12 }}>
            {pub.description.length > 100 ? pub.description.slice(0, 100) + '...' : pub.description}
          </p>
        )}

        {pub.tags?.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
            {pub.tags.map(tag => (
              <span key={tag} style={{
                padding: '3px 10px', background: 'rgba(102,153,255,0.04)',
                border: '1px solid rgba(102,153,255,0.1)',
                fontSize: 10, fontWeight: 600, color: C.blue,
              }}>
                {tag}
              </span>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 12, borderTop: `1px solid ${C.creamWarm}` }}>
          <div style={{ fontSize: 11, color: C.textMuted }}>
            {pub.year} · {formatSize(pub.file_size)}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={onPreview}
              style={{
                padding: '6px 14px', border: `1px solid ${C.blue}`, background: 'transparent',
                color: C.blue, fontFamily: "'Manrope', sans-serif", fontSize: 10, fontWeight: 700,
                letterSpacing: 1, textTransform: 'uppercase', cursor: 'pointer',
              }}
            >
              Preview
            </button>
            <a href={getUrl(pub.file_path)}
              download
              target="_blank"
              rel="noreferrer"
              style={{
                padding: '6px 14px', background: C.blue, border: 'none',
                color: C.navy, fontFamily: "'Manrope', sans-serif", fontSize: 10, fontWeight: 700,
                letterSpacing: 1, textTransform: 'uppercase', textDecoration: 'none',
                display: 'inline-flex', alignItems: 'center',
              }}
            >
              Download
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}